import type { ReactNode } from 'react';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import FileDescriptionTextArea from '../../../../common/components/file-description-text-area';
import { RequiredMark } from '../../../../common/components/required-mark';
import { UploadField } from '../../../../common/components/upload-field';
import type { UploadFieldElement } from '../../../../types/form/Element';
import {
  useFormSubmitterContext,
  FORM_SUBMITTER_ACTIONS,
} from '../../form-submitter-context';
import { isFileUploadDescriptionValidationError } from '../../FormValidator';

type Props = {
  description?: ReactNode;
  element: UploadFieldElement;
};

export const UploadFieldWithContext = (props: Props) => {
  const intl = useIntl();
  const { description, element } = props;
  const {
    id,
    disabled,
    label,
    required,
    visible,
    showFileDescription = false,
  } = element;
  const {
    state,
    dispatch,
    selector: { getItemsForId, getErrorForId },
  } = useFormSubmitterContext();

  const items = getItemsForId(state, id);
  const { submitInProgress, submissionFailed, response = {} } = state;
  const error = getErrorForId(state, id);
  const isDisabled = submitInProgress || submissionFailed;
  const fileDescription = response[id]?.description || '';
  const handleAddItems = useCallback(
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
    newItems => {
      dispatch({
        id,
        type: FORM_SUBMITTER_ACTIONS.ADD_ITEMS,
        items: newItems,
      });
    },
    [dispatch, id],
  );

  const handleRemoveItem = useCallback(
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
    item => {
      dispatch({
        id,
        type: FORM_SUBMITTER_ACTIONS.REMOVE_ITEM,
        item,
      });
    },
    [dispatch, id],
  );

  const handleChangeFileDescription = useCallback(
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
    event => {
      if (!showFileDescription) {
        return;
      }
      dispatch({
        id,
        type: FORM_SUBMITTER_ACTIONS.UPLOAD_FIELD_FILE_DESCRIPTION_ON_CHANGE,
        newValue: event.target.value,
      });
    },
    [dispatch, id, showFileDescription],
  );

  const renderLabel = required ? (
    <>
      {label} <RequiredMark />
    </>
  ) : (
    label
  );

  const isDescError = error?.id
    ? isFileUploadDescriptionValidationError(error.id)
    : false;

  return visible !== false ? (
    <>
      <UploadField
        elementId={id}
        description={description}
        error={error && !isDescError ? error : undefined}
        isDisabled={disabled || isDisabled}
        isRequired={required}
        items={items}
        label={renderLabel}
        onAddItems={handleAddItems}
        onRemoveItem={handleRemoveItem}
      />
      {showFileDescription ? (
        <FileDescriptionTextArea
          disabled={false}
          error={error && isDescError && intl.formatMessage(error)}
          isRequired={required}
          onChange={handleChangeFileDescription}
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          value={fileDescription}
        />
      ) : null}
    </>
  ) : null;
};
